import sanitizeHtml from 'sanitize-html-react';
import config from 'config';
import moment from 'moment';
import i18n from 'i18n/en.json';

const sanitize = dirty => {
    return {
        __html: sanitizeHtml(dirty, {
            allowedTags: config.allowedTags,
            allowedAttributes: config.allowedAttributes,
            allowedSchemes: config.allowedSchemes
        })
    };
};

//export default sanitize;

const staticUrl = url => {
    return window.__INITIAL__.staticLibraryURL
        ? window.__INITIAL__.staticLibraryURL + '/' + url
        : url;
};

const fetchFromApi = (url, handle, data = {}, method) => {
    let fetchUrl = `${url}?` + encodeURI(Object.keys(data).reduce((res, key, ind) => res + (ind > 0 ? '&' : '') + `${key}=${data[key]}`, ''));
    const options = {
        method: method || 'GET',
        credentials: 'same-origin' //must have to send cookies
    };

    if (method === 'POST') {
        options.body = JSON.stringify(data);
        fetchUrl = url;
    }

    return fetch(
        fetchUrl,
        options
    )
        .then(response => {
            if (!response.ok) {
                throw Error(response.statusText);
            }
            return response.json();
        })
        .then(jsonResponse => {
            if (jsonResponse.error && jsonResponse.error === 500) {
                if (jsonResponse.errorTitle) {
                    throw new ExceptionWithTitle(jsonResponse.errorTitle, jsonResponse.errorMsg);
                } else {
                    throw Error(jsonResponse.errorMsg);
                }
            } else if ('Success' in jsonResponse && !jsonResponse.Success) {
                throw Error(jsonResponse.Message);
            }
            return jsonResponse;
        })
        .then(handle)
        .catch(err => {
            if(err.title) {
                throw new ExceptionWithTitle(err.title, err.message);
            } else {
                throw new Error(err.message);
            }
        });
};

const formatPhoneNumber = phoneNumberString => {
    let isInt = phoneNumberString[0] === '+';
    let cleaned = isInt
        ? '+' + ('' + phoneNumberString).replace(/\D/g, '')
        : ('' + phoneNumberString).replace(/\D/g, '');
    let match = isInt
        ? cleaned.match(/^(\+1|)(\d{3})(\d{3})(\d{4})$/)
        : cleaned.match(/^(\1|)(\d{1})(\d{3})(\d{3})(\d{4})$/);

    if (match && match[1]) {
        return [match[1], ' ', match[2], ' ', match[3], '-', match[4]].join('');
    } else if (match && !match[1] && match[2] === '1') {
        return [match[2], ' (', match[3], ') ', match[4], '-', match[5]].join('');
    } else if (match && !match[1] && phoneNumberString[0] !== '+') {
        return ['(', match[2], ') ', match[3], '-', match[4]].join('');
    }

    return phoneNumberString;
};

const getAddressString = (storeInformation) => {
    let result = '';

    if (!storeInformation) {
        return result;
    }
    result = storeInformation.postalCode + ' ' + storeInformation.address1 + ' ' + storeInformation.city + ', ' + storeInformation.stateCode;

    return result;
};

const getCalendarUrlParams = (props, gmail) => {
    let title = i18n.confirmationView.calendarEventTitle;
    let services = Object.values(props.services).reduce(
        (all, service) => {return all+', '+service.name}, ''
    ).slice(2);
    title += services;
    let format = 'YYYYMMDD[T]HHmmss[Z]';
    let appointmentDuration = props.calendarEventAppointmentDuration;
    let result = '';
    if (!props || !props.storeInformation || !props.time || !props.user) {
        return result;
    }

    let address = getAddressString(props.storeInformation);
    let dateStart = moment.utc(props.time).format(format);
    let dateEnd = moment.utc(props.time).add(appointmentDuration, 'hours').format(format);

    if (gmail) {
        result = '?action=TEMPLATE&dates=' + dateStart + '/' + dateEnd + '&text=' + title + '&location=' + address + '&details=' + services;
    } else {
        result = '?address=' + address + '&description=' + services + '&end=' + dateEnd + '&start=' + dateStart + '&summary=' + services + '&timezone=America/Los_Angeles&creator=' + props.user.email;
    }

    return result;
};

const setModalNoScroll = (isOpen) => {
    const noScrollClassName = window.innerWidth <= config.breakpoints.maxMobile ? 'no-scroll' : 'no-scroll-desktop';

    if (isOpen) {
        document.body.classList.add(noScrollClassName); //turn off scroll for body
        document.documentElement.classList.add(noScrollClassName); //turn off scroll for html
    } else {
        document.body.classList.remove(noScrollClassName); //turn on scroll for body
        document.documentElement.classList.remove(noScrollClassName); //turn on scroll for html
    }
};

/**
 * @description Return a form fields names and values as array
 *
 * @param {object} form Form DOM element
 * @param {Boolean} removeSearchQuery add or not 'sq' value to the array
 *
 * @return {Object} [{name: 'test', value: 'test'}]
 */
const getFormEntries = (form, removeSearchQuery) => {
    let formEntries = [];

    if (!form) {
        return formEntries;
    }

    for (let i = 0; i < form.elements.length; i++) {
        let element = form.elements[i];

        if (
            !element.name ||
            element.disabled ||
            element.type === 'submit' ||
            element.type === 'button' ||
            (removeSearchQuery && element.name === 'sq')
        ) {
            continue;
        }

        formEntries.push({name: element.name, value: element.value});
    }

    return formEntries;
}

/**
 * @param {Object} breadcrumbs
 *
 * @return {String} 'width', 'rim', 'ratio'
 */
const getFrontTireFinishedBreadcrumb = (breadcrumbs) => {
    let bredcrumb = 'width';

    if (!breadcrumbs) {
        return bredcrumb;
    } 

    if (breadcrumbs.rim.isComplete) {
        bredcrumb = 'rim';
    } else if (breadcrumbs.ratio.isComplete) {
        bredcrumb = 'ratio';
    }

    return bredcrumb;
}

/**
 * @param {String} url - Page-Show?cid=pdp-tire-our-promise
 */
const updateStoremanagerBlock = (url) => {
    if (!url) {
        return false;
    }

    fetch(url)
        .then(response => {
            if (!response.ok) {
                throw Error(response.statusText);
            }
            return response.text();
        })
        .then(data => {
            if (data.trim().length > 0) {
                var $response = $(data);

                let $contentBody = $response.find('.js-our-promise');
                let $storeManagerBlock = $(document).find('.js-our-promise');
                if ($contentBody.length && $storeManagerBlock.length) {
                    $storeManagerBlock.html($contentBody.html());
                }
            }
        })
        .catch(err => {
            if (err.title) {
                throw new ExceptionWithTitle(err.title, err.message);
            } else {
                throw new Error(err.message);
            }
        });
}

/**
 * @function getTireTitle
 * @description Return tire title
 * @param {Object} option tire data
 * @returns {string} tire title
 */
const getTireTitle = (option) => {
    if (option.Front.Description !== option.Rear.Description) {
        return `${i18n.selectionFlows.frontLabel} ${option.Front.Description} ${i18n.selectionFlows.rearLabel} ${option.Rear.Description}`;
    }
    return option.Front.Description;
}

export {
    staticUrl,
    sanitize,
    fetchFromApi,
    formatPhoneNumber,
    getCalendarUrlParams,
    setModalNoScroll,
    getFormEntries,
    getFrontTireFinishedBreadcrumb,
    updateStoremanagerBlock,
    getTireTitle
};

function ExceptionWithTitle(title, message) {
    this.title = title;
    this.message = message;
}
